import * as Tone from 'tone';
import { getCurrentProject } from './sessionManager';
import { midiHandler } from './MidiHandler';
import store, {
    PROJECT_PARAMS_DESCRIPTION,
    selectBpm,
    selectCompas,
    selectReturns,
    selectIsRecordingProject,
    selectSwing,
    selectTonality,
    setProjectParameter,
    setTransportParameter,
    selectIsRecordArmed,
    selectIsPlaying,
    selectIsMetronomeEnabled,
    selectTransportCurrentStep,
    selectTransportSync,
    setAudioParameter,
    selectUsesAudioEngine,
    selectIsGraphBuilt,
    AUDIO_PARAMS_DESCRIPTION,
    selectSampleRate,
    selectPlaybackMode,
    dequeueSlots,
    setProjectParameter,
    setMasterChannelParameter,
    createMasterChannelParameterSelector,
    selectScenes,
    transportStop,
    launchScene,
    selectCurrentSceneIDAndStep,
    selectPlayStartMarkerStep,
    selectInstrumentIDs
} from './store';
import { initializeWamHost } from '@webaudiomodules/sdk';
import defaults from "./defaults.json"
import { ReverbReturn } from './channels/ReverbReturn';
import { DelayReturn } from './channels/DelayReturn';
import { MasterChannel } from './channels/MasterChannel';
import { downloadedAudioRecordingFilename } from "$utils/misc";
import { importModuleFromExternalUrl } from './utils/import';
import { staticSelect } from './utils/react';
import { dx2x } from './utils/array';


// Patch global scope with "custom" AudioWorkletNode and GainNode versions from standardized-audio-context.
// to ensure that WAM plugins can find the expected nodes when they are created. This is needed because Tone.js can wrap the native audio context in a way that makes it so that WAM plugins can't find the expected AudioWorkletNode and GainNode constructors when they try to create them. By patching the global scope with the standardized-audio-context versions of these nodes, we ensure better compatibility with WAM plugins. Note that this patching is only needed for the AudioWorkletNode parameters property, which is expected to be a Map in WAM plugins but is a plain object in some versions of Tone.js. The GainNode patching is just for consistency and to ensure that WAM plugins can find the expected GainNode constructor as well.

import { AudioWorkletNode, GainNode } from 'standardized-audio-context';
class PatchedAudioWorkletNode extends AudioWorkletNode {
    get parameters () {
        return super.parameters._map;
    }
}
window.AudioWorkletNode = PatchedAudioWorkletNode;
window.GainNode = GainNode;


export const getAudioGraphInstance = () => {
    return audioGraph;
}


const createRecorderNode = async () => {
    await Tone.getContext().addAudioWorkletModule(appPrefix + "/static/dist/worklets/recorder.js");
    const node = Tone.getContext().createAudioWorkletNode('recorder-worklet');
    node.port.onmessage = (evt) => {
        console.debug("Received recording of duration", evt.data.recordingLength)
        const audioBlob = new Blob([evt.data.dataView], { type: 'audio/wav' }); 
        const url = URL.createObjectURL(audioBlob);
        const anchor = document.createElement("a");
        anchor.download = downloadedAudioRecordingFilename();
        anchor.href = url;
        anchor.click();
    }
    node.startRecording = () => {
        node.port.postMessage({eventType: 'startRecording'});
    }
    node.stopRecording = () => {
        node.port.postMessage({eventType: 'stopRecording', sampleRate: Tone.getContext().sampleRate});
    }
    return node
}

export class AudioGraph {
    constructor() {
        this.remoteTransportCurrentStep = -1;  // Aquest parametre no el posem a l'store perquè no volem que es propagui a la UI
        this.returns = {};
        Tone.getContext().dispose();
        Tone.getContext().rawContext.close(); // close the initial audio context that tone creates
        this.wamHostGroupIds = {};
    }
    
    get parametersDescription() {
        return {...PROJECT_PARAMS_DESCRIPTION, ...AUDIO_PARAMS_DESCRIPTION};
    }

    getParameterDescription(parameterName) {
        return this.parametersDescription[parameterName]
    }
    
    setParameterValue(nomParametre, valor) {
        const methodName = `set${nomParametre.charAt(0).toUpperCase() + nomParametre.slice(1)}`;
        this[methodName](valor);
    }
    
    usesAudioEngine() {
        return staticSelect(selectUsesAudioEngine);
    }

    async turnAudioOn() {
        await this.startAudioContext();
        if (!this.isGraphBuilt()) await this.buildAudioGraph();
    }

    turnAudioOff() {
        if (this.isGraphBuilt()) this.disposeGraph();
        this.clearWamHostGroupIds();
        if (Tone.getContext().rawContext.state !== "closed") {
            Tone.getContext().dispose();
            Tone.getContext().rawContext.close();
        }
    }

    restartAudio() {
        this.turnAudioOff();
        this.turnAudioOn();
    }

    disposeGraph() {
        console.info("Disposing audio graph");
        this.#setIsGraphBuilt(false);

        this.masterChannel.dispose();
        this.metronome.dispose();
        this.mainSequencer.dispose();

        Object.values(this.returns).forEach(returnNode => returnNode.dispose());
        
        staticSelect(selectInstrumentIDs).forEach(instrumentID => this.removeInstrumentFromAudioGraph(instrumentID));
    }

    
    isPlaying() {
        return staticSelect(selectIsPlaying);
    }
    
    isRecording() {
        return staticSelect(selectIsRecordingProject);
    }
    
    setIsRecording(value) {
        store.dispatch(setTransportParameter({name: 'isRecordingProject', value}));
    }
    
    isRecordArmed() {
        return staticSelect(selectIsRecordArmed);
    }
    
    setIsRecordArmed(value) {
        store.dispatch(setTransportParameter({name: 'isRecordArmed', value}));
    }
    
    startRecordingProject() {
        if (!this.isGraphBuilt()) return;
        if (this.isRecording()) return;
        if (this.isRecordArmed()) return;
        if (this.isPlaying()) {
            this.projectRecorderNode.startRecording();
            this.setIsRecording(true);
        } else {
            this.setIsRecordArmed(true);
        }
    }
    
    stopRecordingProject() {
        if (!this.isGraphBuilt()) return;
        if (!this.isRecording()) return;
        this.projectRecorderNode.stopRecording();
        this.setIsRecording(false);
        this.setIsRecordArmed(false);
    }

    getMasterSpectrumSize() {
        if (!this.masterChannel) return undefined;
        return this.masterChannel.spectrumSize;
    }
    
    getMasterSpectrumData() {
        if (!this.masterChannel) return undefined;
        return this.masterChannel.spectrumData;
    }
    
    getBpm() {
        return staticSelect(selectBpm)
    }
    
    applyBpm(bpm) {
        if (!this.isGraphBuilt()) return;
        Tone.getTransport().bpm.rampTo(bpm);
    }
    
    getSwing(){
        return staticSelect(selectSwing)
    }

    getCompas(){
        return staticSelect(selectCompas)
    }
    
    getTonality(){
        return staticSelect(selectTonality)
    }

    isGraphBuilt() {
        return staticSelect(selectIsGraphBuilt)
    }

    #setIsGraphBuilt(value) {
        store.dispatch(setAudioParameter({name: 'isGraphBuilt', value}))
    }
    
    isTransportSyncMaster() {
        return staticSelect(selectTransportSync) === 'send';
    }
    
    setTransportSync(value) {
        store.dispatch(setTransportParameter({name: 'sync', value}));
    }

    isTransportSyncFollower() {
        return staticSelect(selectTransportSync) === 'receive';
    }
    
    setTransportCurrentStep(currentStep) {
        store.dispatch(setTransportParameter({name: 'currentStep', value: currentStep}));
    }
    
    getTransportCurrentStep() {
        return staticSelect(selectTransportCurrentStep)
    }
    
    getCurrentLevelFxReturn(nomFx) {
        if (!this.isGraphBuilt()) return -60;
        if (nomFx === 'reverbA') return this.returns.reverbA.getCurrentLevel();
        if (nomFx === 'reverbB') return this.returns.reverbB.getCurrentLevel();
        if (nomFx === 'delayA') return this.returns.delayA.getCurrentLevel();
        if (nomFx === 'delayB') return this.returns.delayB.getCurrentLevel();
        return -60;
    }
    
    getCurrentMasterLevels() {
        if (!this.isGraphBuilt()) return [-60, -60];
        return this.masterChannel.getCurrentLevels();
    }

    //Creem un metronom
    
    initMetronome() {
        this.metronome = new Tone.NoiseSynth({
            volume: -6, 
            envelope: {
                attack: 0.001,
                decay: 0.1,
                sustain: 0,
            }
        }).toDestination();
    }
    
    // Creem els returns
    initReturns() {
        const reverbA = new ReverbReturn().connect(this.masterChannel);
        const reverbB = new ReverbReturn().connect(this.masterChannel);
        const delayA = new DelayReturn().connect(this.masterChannel);
        const delayB = new DelayReturn().connect(this.masterChannel);

        this.returns = { reverbA, reverbB, delayA, delayB };
    }
    
    get16BeatTime() {
        return 60 / (this.getBpm() * 4);
    }
    
    setIsMetronomeEnabled(value) {
        store.dispatch(setTransportParameter({name: 'isMetronomeEnabled', value}));
    }
    
    isMetronomeEnabled() {
        return staticSelect(selectIsMetronomeEnabled)
    }

    async addInstrument(instrumentID, value) {
        await this.#addInstrumentToAudioGraph(instrumentID);
    }
    
    removeInstrument(instrumentID) {
        this.#removeInstrumentFromAudioGraph(instrumentID);
    }
    
    async #addInstrumentToAudioGraph(instrumentID) {
        if (!this.isGraphBuilt()) return;
        const instrument = getCurrentProject().getInstrument(instrumentID);
        await instrument.buildInstrumentAudioGraph();
        instrument.bus.connect(this.masterChannel);
        // Carrega parameter values a l'audio graph
        instrument.applyStateToAudioGraph();
    }

    #removeInstrumentFromAudioGraph(instrumentID) {
        if (!this.isGraphBuilt()) return;
        getCurrentProject().getInstrument(instrumentID).dispose();
    }
    
    async buildAudioGraph() {
        console.debug("Building audio graph")
        this.#setIsGraphBuilt(false);
        this.setTransportCurrentStep(-1);
        
        // Setteja el bpm al valor guardat
        Tone.getTransport().bpm.value = this.getBpm();
        
        this.masterChannel = new MasterChannel().toDestination();
        this.masterChannel.gain = staticSelect(createMasterChannelParameterSelector("gain"))
        this.masterChannel.pan = staticSelect(createMasterChannelParameterSelector("pan"))
        
        // Crea el node "loop" principal per marcar passos a les instruments que segueixen el sequenciador
        this.mainSequencer = new Tone.Loop(time => {
            if (this.isPlaying()) this.onMainSequencerStep(time);
        }, "16n").start(0);
        
        // Inicialitzem els returns
        this.initReturns();
        
        //Inicialitzem metronom
        this.initMetronome(); 
        
        // Marca el graph com a construït
        this.#setIsGraphBuilt(true);

        // Afegir instruments a l'audio graph no és part del 'nucli' de l'audio graph. Els instruments al llarg del projecte es poden afegir
        // i treure, i a més, els instruments necessiten saber si el 'nucli' de l'audio graph està construit (isGraphBuilt) per poder
        // ajustar els seus paràmetres
        await Promise.all(
            staticSelect(selectInstrumentIDs).map(instrumentID => this.#addInstrumentToAudioGraph(instrumentID))
        );
        
        
        // Carrega els paràmetres dels efectes generals

        Object.entries(staticSelect(selectReturns)).forEach(([returnName, {parameters}]) => {
            Object.entries(parameters).forEach(([parameterName, value]) => this.returns[returnName][parameterName] = value);
        })
    }

    clearWamHostGroupIds = () => {
        // TODO: this clear the list of "saved" hosts, but does not destroy the hosts themselves. It might not
        // bee too relevant as users will not be switching audio engine on/off all the time, but would be nice to
        // see how to properly destroy them.
        this.wamHostGroupIds = {};
    }

    async initializeWamHost(hostIdGRUF) {
        // Initializes a host for WAM plugins
        // Each host can have a chain of plugins, and if MIDI is sent to a plugin in the chain, it will be cascaded to the rest of plugins in the chain.
        // For that reason it might be convenient to have different hosts for different chains of plugins and this can be done using the "hostIdGRUF" parameter.
        // If this method is called multiple times with the same hostIdGRUF, it will return the coresponding WAM hostGroupId without re-initializing.
        // hostGroupId is later used in initWamInstance to specify in which host a WAM plugin should be initialized.
        if (this.wamHostGroupIds[hostIdGRUF]) {
            return this.wamHostGroupIds[hostIdGRUF];
        }
        const [hostGroupId] = await initializeWamHost(Tone.getContext().rawContext)
        this.wamHostGroupIds[hostIdGRUF] = hostGroupId
        return hostGroupId;
    }

    async initWamInstance(wamUrl, hostIdGRUF='default') {
        // Loads a specific wam plugin from a URL
        const {default: WAM} = await importModuleFromExternalUrl(wamUrl);

        // Initialize WAM host for this plugin (or reuses an alreay initialized host with the same hostIdGRUF), and initialize the instance of the WAM plugin in that host
        const wamHostGroupId = await this.initializeWamHost(hostIdGRUF);
        return await WAM.createInstance(wamHostGroupId, Tone.getContext().rawContext);
    }
    
    async startAudioContext() {
        // Optimize global tone context for playback (mightier latency and use less CPU (?))
        let latencyHintParam = undefined;
        const paramName = "latencyHint";
        var queryString = location.search
        let params = new URLSearchParams(queryString)
        if (params.has(paramName)) {
            latencyHintParam = params.get(paramName)
        }        
        let latencyHintValue = latencyHintParam !== undefined ? latencyHintParam: "interactive"
        if (!isNaN(latencyHintValue)) {
            latencyHintValue = parseFloat(latencyHintValue)  // In case latency hint is passed as a number in seconds
        }
        console.debug("Starting audio context with latency hint: ", latencyHintValue);
        
        const context = new Tone.Context(new Tone.context.rawContext.constructor({ latencyHint: latencyHintValue, sampleRate: staticSelect(selectSampleRate) }));
        Tone.setContext(context);        
        await Tone.start()
        console.debug("Tonejs and audio context started");

        this.projectRecorderNode = await createRecorderNode();
        Tone.getDestination().connect(this.projectRecorderNode);
        console.debug("Recorded node initialized");
    }
    
    async transportStart() {
        if (!this.isGraphBuilt()) return;

        let transportCurrentStep = 0;
        if (this.isTransportSyncFollower()) transportCurrentStep = this.remoteTransportCurrentStep;
        else if (staticSelect(selectPlaybackMode) !== "followInstruments") transportCurrentStep = staticSelect(selectPlayStartMarkerStep);
        this.setTransportCurrentStep(transportCurrentStep);
        
        // Comença a gravar si estava record armed
        if (this.isRecordArmed()) {
            this.projectRecorderNode.startRecording();
            this.setIsRecordArmed(false);
            this.setIsRecording(true);
        }
        
        // Trigueja el transport start a totes les instruments i el transport general
        this.didTransportJustStart = true;
        Tone.getTransport().start();
    }
    
    transportStop() {
        if (!this.isGraphBuilt()) return;
        Tone.getTransport().stop()

        // Cridem "onTransportStop" de totes les instruments per assegurar que s'aturen tots els sons
        staticSelect(selectInstrumentIDs).forEach(instrumentID => {
            const instrument = getCurrentProject().getInstrument(instrumentID);
            instrument.onTransportStop();
        });
        
        // Atura la gravació si estava gravant
        if (this.isRecording()) {
            this.stopRecordingProject();
        }
        
        this.setTransportCurrentStep(-1);
    }
    
    onMainSequencerStep(time) {
        let currentStep = this.getTransportCurrentStep();
        if (this.didTransportJustStart) {
            // we don't advance a step in the first iteration
            // because the step we want was previously set
            this.didTransportJustStart = false;
        } else {
            currentStep += 1;
            this.setTransportCurrentStep(currentStep);
        }
        const beatInBar = Math.floor(currentStep / 4) % 4;
        const subdivisionInBeat = currentStep % 4;

        const playbackMode = staticSelect(selectPlaybackMode);
        // First, dequeue slots if needed. instrument.onSequencerStep needs the updated clip information
        if (playbackMode === "followLauncher") {
            if (beatInBar === 0 && subdivisionInBeat === 0) store.dispatch(dequeueSlots({}));
            if (!this.isPlaying()) return; // could be that with the slot dequeuing, there are no more slots and transport stop is called
        }
        else if (playbackMode === "followArrangement") {
            const { sceneID, step: currentSceneStep } = staticSelect(selectCurrentSceneIDAndStep);
            if (sceneID == null) {
                store.dispatch(transportStop({}));
                return;
            }
            if (currentSceneStep === 0 || this.lastLaunchedScene !== sceneID) {
                store.dispatch(launchScene({sceneID}));
                this.lastLaunchedScene = sceneID; // per si les duracions de les escenes canvien!
            }
            currentStep = currentSceneStep;
        }

        // Trigueja el step del sequenciador a totes les instruments
        // amb el referent de temps actual i el beat general. Les instruments s'encarreguen
        // de transformar el número de beat global a la seva duració interna
        staticSelect(selectInstrumentIDs).forEach(instrumentID => {
            const instrument = getCurrentProject().getInstrument(instrumentID);
            instrument.onSequencerStep(currentStep, time);
        });

        if (this.isMetronomeEnabled()) {
            if (subdivisionInBeat === 0) {
                this.metronome.triggerAttackRelease("16n", time);
            }
        }

    }
    
    panic(instrumentID) {
        if (!this.isGraphBuilt()) return;
        
        if (instrumentID === undefined) {
            // Stop any notes/sounds being played in all stations, clear "caches" of current notes being played
            staticSelect(selectInstrumentIDs).forEach(instrumentID => {
                const instrument = getCurrentProject().getInstrument(instrumentID);
                instrument.onStopAllSounds();
            });
        } else {
            // Stop any notes/sounds being played in the specified station, clear "caches" of current notes being played
            const instrument = getCurrentProject().getInstrument(instrumentID);
            instrument.onStopAllSounds();
        }
        midiHandler.clearNotes();
    }

    updateProjectParameter(name, value) {
        store.dispatch(setProjectParameter({name, value}));
    }

    updateAudioParameter(name, value) {
        store.dispatch(setAudioParameter({name, value}));
    }

    updateTransportParameter(name, value) {
        store.dispatch(setTransportParameter({name, value}));
    }
    
    receiveRemoteTransportCurrentStep(currentStep) {
        this.remoteTransportCurrentStep = currentStep;
        if (this.isTransportSyncFollower() && !this.isPlaying()){
            this.setTransportCurrentStep(currentStep);
        }
    }
}

const audioGraph = new AudioGraph();
        